import { useState, useEffect } from "react";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { H4 } from "@/src/components/Heading";
import { Button } from "@/src/components/tailwind/Button";
import { useSelector } from "react-redux";
import { authSelectors } from "@/src/store/auth/selector";
import {
  Deal,
  DealStatus,
  useAllDataRoomFoldersQuery,
  useCopyDataRoomFolderDealStructureMutation,
  useDataRoomFastQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { buildFolderHierarchy } from "@/src/utils/buildFolderHierarchy";
import { FolderContent } from "./tree_view";
import Loading from "@/src/components/Loading";
import { DataRoomContext } from ".";
import { useParams } from "react-router-dom";
import { toasts } from "@/src/components/toasts/toasts";
import { useQueryClient } from "@tanstack/react-query";
import { DealCard } from "@/src/routes/home";
import { TextInput } from "@/src/components/tailwind/TextInput";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";

interface CopyFolderStructureModalProps {
  open: boolean;
  onClose: () => void;
}

export function CopyFolderStructureModal(props: CopyFolderStructureModalProps) {
  const [selectedDeal, setSelectedDeal] = useState<Partial<Deal> | null>(null);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [depth, setDepth] = useState<number>(1);

  const client = useGqlClient();
  const queryClient = useQueryClient();
  const { invalidateDataRoomFolders } = useInvalidateQueryKeys();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const account = useSelector(authSelectors.account);
  const deals = account?.deals.filter(
    (deal) => deal.status === DealStatus.Active,
  );

  const [searchTerm, setSearchTerm] = useState("");

  // Get current deal's data room information
  const currentDataRoomQuery = useDataRoomFastQuery(client, {
    dealId: activeDealId || "",
  });

  // Get selected deal's data room information
  const selectedDataRoomQuery = useDataRoomFastQuery(
    client,
    {
      dealId: selectedDeal?.id || "",
    },
    { enabled: !!selectedDeal?.id },
  );

  const allFoldersQuery = useAllDataRoomFoldersQuery(
    client,
    { dealId: selectedDeal?.id || "" },
    { enabled: !!selectedDeal?.id },
  );

  const copyFolderStructure =
    useCopyDataRoomFolderDealStructureMutation(client);

  useEffect(() => {
    if (!props.open) {
      setSelectedDeal(null);
      setSelectedFolders([]);
      setDepth(1);
    }
  }, [props.open]);

  const handleCopy = () => {
    if (
      !selectedDeal?.id ||
      !currentDataRoomQuery.data ||
      !selectedDataRoomQuery.data
    ) {
      toasts.error("Please select a deal and wait for data to load");
      return;
    }

    const fromDataRoomId = selectedDataRoomQuery.data.deal.dataRoom.id;
    const toDataRoomId = currentDataRoomQuery.data.deal.dataRoom.id;

    copyFolderStructure.mutate(
      {
        input: {
          fromDataRoomID: fromDataRoomId,
          toDataRoomID: toDataRoomId,
          name: `Copied from ${selectedDeal.company?.name || selectedDeal.id}`,
          depth: depth
        },
      },
      {
        onSuccess: () => {
          toasts.success("Folder structure copied successfully");

          invalidateDataRoomFolders();
          queryClient.invalidateQueries({
            queryKey: ["DataRoomFast", { dealId: activeDealId }],
          });

          props.onClose();
        },
        onError: (error) => {
          console.error("Error copying folder structure:", error);
          toasts.error("Failed to copy folder structure. Please try again.");
        },
      },
    );
  };

  const filteredFolders =
    allFoldersQuery.data?.deal.dataRoom.allFolders.filter(
      (folder) => folder.parentFolders.length <= depth + 1,
    ) || [];

  const folderHierarchy = allFoldersQuery.data
    ? buildFolderHierarchy(filteredFolders, false, [], [], {})
    : [];

  let activeDeals = deals?.filter((deal) => deal.id !== activeDealId);
  if (searchTerm.length > 0) {
    activeDeals = activeDeals?.filter((deal) =>
      deal.company.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }


  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="xl">
      <div className="flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between mb-6">
          <H4>Copy deal structure</H4>
          <CloseIcon onClose={props.onClose} />
        </div>

        <div>
          <TextInput
            placeholder="Search for a deal..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="border border-gray-200 rounded-md p-3 bg-gray-50 mt-5 overflow-y-scroll h-[25vh]">
          <div className="flex flex-wrap mt-2 gap-4">
            {activeDeals?.map((deal) => {
              return (
                <div
                  key={deal.id}
                  className={`cursor-pointer text-left rounded-lg border-2 transition-all min-w-0 lg:min-w-[calc(50%-0.5rem)] lg:max-w-[calc(50%-0.5rem)] h-16 ${selectedDeal?.id === deal.id
                    ? "border-blue-500 bg-blue-50"
                    : "border-transparent hover:border-gray-300"
                    }`}
                  onClick={() => setSelectedDeal(deal as Partial<Deal>)}
                >
                  <DealCard
                    deal={deal as Deal}
                    truncate={true}
                    activeLink={false}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {selectedDeal && (
          <div className="mt-4 ">
            <div className="flex items-center justify-between gap-4 mb-2">
              <label className="text-sm font-semibold leading-6 text-gray-900 shrink-0">
                Folder structure preview
              </label>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs text-gray-500 mr-1">Depth:</span>
                <div
                  className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
                  role="radiogroup"
                  aria-label="Copy depth (folder levels)"
                >
                  {([1, 2, 3, 4, 5] as const).map((d) => (
                    <button
                      key={d}
                      type="button"
                      role="radio"
                      aria-checked={depth === d}
                      aria-label={`${d} level${d === 1 ? "" : "s"}`}
                      className={`min-w-[28px] px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                        depth === d
                          ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-transparent"
                      }`}
                      onClick={() => setDepth(d)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="border border-gray-200 rounded-md p-3 max-h-64 bg-gray-50 h-[20vh] overflow-y-scroll">
              {allFoldersQuery.isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <Loading />
                </div>
              ) : allFoldersQuery.error ? (
                <p className="text-sm text-red-600">
                  Failed to load folder structure
                </p>
              ) : folderHierarchy.length === 0 ? (
                <p className="text-sm text-gray-500">No folders found</p>
              ) : (
                <DataRoomContext.Provider
                  value={{
                    indexingEnabled: false,
                    selectedFiles: [],
                    selectedFolders,
                    setSelectedFolders,
                    setSelectedFiles: () => {},
                    filters: {},
                    setFilters: () => {},
                    folderPills: new Map(),
                  }}
                >
                  {folderHierarchy.map((folder) => (
                    <FolderContent
                      key={folder.folder.id}
                      folder={folder}
                      searchTerm=""
                      mode="latest"
                      depth={0}
                      showChildFiles={false}
                      showChevron={false}
                      showCheckbox={false}
                    />
                  ))}
                </DataRoomContext.Provider>
              )}
            </div>
            {selectedFolders.length > 0 && (
              <p className="text-sm text-gray-600 mt-2">
                {selectedFolders.length} folder
                {selectedFolders.length !== 1 ? "s" : ""} selected
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="neutral" text="Cancel" onClick={props.onClose} />
          <Button
            variant="positive"
            text="Copy structure"
            onClick={handleCopy}
            isDisabled={!selectedDeal || !currentDataRoomQuery.data}
            isLoading={copyFolderStructure.isPending}
            loadingText="Copying..."
          />
        </div>
      </div>
    </AnimatedModal>
  );
}
